<template lang="pug">
.tab-dealer
  h3 Wil je een dealer koppelen?
  p.mb-0 Activeer hieronder een dealer indien de aanvragen via een externe dealer binnenkomen.

  v-layout(align-center)
    v-switch(
      :label='data.dealerEnabled ? "Ingeschakeld" : "Uitgeschakeld"',
      v-model='data.dealerEnabled',
      style='max-width: 150px',
      color='primary'
      )

  v-expansion-panel.elevation-0(v-model='expanded', disabled)
    v-expansion-panel-content
      v-layout(align-center, slot='header')
        transition(name='fade', mode='out-in')
          v-icon.mr-3(v-if='data.dealerEnabled' color='green', key='enabled') fa fa-check
          v-icon.mr-3(v-else, key='disabled') fa fa-ban

        span Dealergegevens

      v-card.mt-4
        v-card-text.pt-0
          v-form(autocomplete='off')
            v-layout
              text-field(
                label='Bedrijfsnaam',
                v-model='data.companyName',
                required,
                name='companyName',
                :error-messages='companyNameValidationErrors',
                @change='$v.data.companyName.$touch()',
                @blur='$v.data.companyName.$touch()'
                )

            v-layout.flex-column-phablet
              text-field(
                label='Voorletters',
                mask='A.A.A.A.A.',
                v-model='data.contactInitials',
                required,
                name='initials',
                :error-messages='contactInitialsValidationErrors',
                @change='$v.data.contactInitials.$touch()',
                @blur='$v.data.contactInitials.$touch()'
                )
              text-field(
                label='Tussenvoegsel',
                v-model='data.contactNameInsertion',
                style='width: 20px'
                )
              text-field(
                label='Achternaam',
                v-model='data.contactLastName',
                required,
                name='lastName',
                :error-messages='contactLastNameValidationErrors',
                @change='$v.data.contactLastName.$touch()',
                @blur='$v.data.contactLastName.$touch()'
                )

            v-layout.flex-column-phablet
              text-field(
                label='Straat',
                v-model='data.streetName',
                required,
                name='streetName',
                :error-messages='streetNameValidationErrors',
                @change='$v.data.streetName.$touch()',
                @blur='$v.data.streetName.$touch()'
                )
              text-field(
                label='Nummer',
                v-model='data.streetNumber',
                required,
                name='streetNumber',
                style='width: 00px',
                :error-messages='streetNumberValidationErrors',
                @change='$v.data.streetNumber.$touch()',
                @blur='$v.data.streetNumber.$touch()'
                )
              text-field(
                label='Toevoeging',
                v-model='data.streetNumberAddition',
                required,
                style='width: 0px'
                )

            v-layout.flex-column-phablet
              text-field(
                label='Plaats',
                v-model='data.city',
                required,
                name='city',
                :error-messages='cityValidationErrors',
                @change='$v.data.city.$touch()',
                @blur='$v.data.city.$touch()'
                )
              text-field(
                label='Postcode',
                v-model='data.postalCode',
                required,
                name='postalCode',
                :error-messages='postalCodeValidationErrors',
                @change='$v.data.postalCode.$touch()',
                @blur='$v.data.postalCode.$touch()'
                )

              v-layout(column)
                label: b Land
                v-autocomplete(
                  :items='countryItems',
                  item-value='short_name',
                  item-text='name',
                  name='country',
                  v-model='data.country',
                  required,
                  solo
                  )

            v-layout.flex-column-phablet
              text-field(
                label='E-mailadres',
                v-model='data.contactEmail',
                required,
                name='email',
                :error-messages='contactEmailValidationErrors',
                @change='$v.data.contactEmail.$touch()',
                @blur='$v.data.contactEmail.$touch()'
                )
              text-field(
                label='Telefoonnummer',
                v-model='data.contactPhone',
                required,
                mask='+## # ## ### ###',
                name='phone',
                :error-messages='contactPhoneValidationErrors',
                @change='$v.data.contactPhone.$touch()',
                @blur='$v.data.contactPhone.$touch()'
                )

            v-layout.flex-column-phablet
              text-field(
                label='KvK Nummer',
                v-model='data.kvkNumber',
                name='kvk',
                :error-messages='kvkNumberValidationErrors',
                @change='$v.data.kvkNumber.$touch()',
                @blur='$v.data.kvkNumber.$touch()'
                )
              text-field(
                label='BTW-Nummer',
                v-model='data.vatNumber',
                name='vat',
                :error-messages='vatNumberValidationErrors',
                @change='$v.data.vatNumber.$touch()',
                @blur='$v.data.vatNumber.$touch()'
                )
</template>

<script>
import countries from 'dealerfinanceservices-shared/src/assets/countries.json'
import { required, email, minLength, maxLength, numeric } from 'vuelidate/lib/validators'
import { fullPhoneRegex } from 'dealerfinanceservices-shared/src/helpers/regex'

export default {
  props: {
    value: Object
  },

  data () {
    return {
      expanded: null,
      data: this.value
    }
  },

  computed: {
    countryItems () {
      return countries
    },

    companyNameValidationErrors () {
      const errors = []
      if (!this.$v.data.companyName.$dirty) return errors
      !this.$v.data.companyName.required && errors.push('Dit veld is verplicht')
      return errors
    },

    streetNameValidationErrors () {
      const errors = []
      if (!this.$v.data.streetName.$dirty) return errors
      !this.$v.data.streetName.required && errors.push('Dit veld is verplicht')
      return errors
    },

    streetNumberValidationErrors () {
      const errors = []
      if (!this.$v.data.streetNumber.$dirty) return errors
      !this.$v.data.streetNumber.required && errors.push('Dit veld is verplicht')
      return errors
    },

    cityValidationErrors () {
      const errors = []
      if (!this.$v.data.city.$dirty) return errors
      !this.$v.data.city.required && errors.push('Dit veld is verplicht')
      return errors
    },

    postalCodeValidationErrors () {
      const errors = []
      if (!this.$v.data.postalCode.$dirty) return errors
      !this.$v.data.postalCode.required && errors.push('Dit veld is verplicht')
      return errors
    },

    kvkNumberValidationErrors () {
      const errors = []
      if (!this.$v.data.kvkNumber.$dirty) return errors
      !this.$v.data.kvkNumber.numeric && errors.push('Dit is geen geldig KvK nummer')
      !this.$v.data.kvkNumber.minLength && errors.push('Dit is geen geldig KvK nummer')
      !this.$v.data.kvkNumber.maxLength && errors.push('Dit is geen geldig KvK nummer')
      return errors
    },

    vatNumberValidationErrors () {
      const errors = []
      if (!this.$v.data.vatNumber.$dirty) return errors
      return errors
    },

    contactInitialsValidationErrors () {
      const errors = []
      if (!this.$v.data.contactInitials.$dirty) return errors
      !this.$v.data.contactInitials.required && errors.push('Dit veld is verplicht')
      return errors
    },

    contactLastNameValidationErrors () {
      const errors = []
      if (!this.$v.data.contactLastName.$dirty) return errors
      !this.$v.data.contactLastName.required && errors.push('Dit veld is verplicht')
      return errors
    },

    contactEmailValidationErrors () {
      const errors = []
      if (!this.$v.data.contactEmail.$dirty) return errors
      !this.$v.data.contactEmail.required && errors.push('Dit veld is verplicht')
      !this.$v.data.contactEmail.email && errors.push('Dit moet een geldig email adres zijn')
      return errors
    },

    contactPhoneValidationErrors () {
      const errors = []
      if (!this.$v.data.contactPhone.$dirty) return errors
      !this.$v.data.contactPhone.required && errors.push('Dit veld is verplicht')
      !this.$v.data.contactPhone.regex && errors.push('Gebruik het volgende formaat: +31 6 12 123 123')
      return errors
    }
  },

  methods: {
    emitValid () {
      let valid = !this.$v.$invalid

      if (!this.data.dealerEnabled) valid = true

      this.$emit('valid', {
        tab: 'tabDealer',
        valid
      })
    }
  },

  watch: {
    value (v) {
      this.data = v
    },

    data: {
      handler: function (v) {
        this.$emit('input', this.data)
      },
      deep: true
    },

    '$v.$invalid': {
      handler: function (v) {
        this.emitValid()
      },
      immediate: true
    },

    'data.dealerEnabled': function (v) {
      this.emitValid()

      this.expanded = v ? 0 : null
    }
  },

  validations: {
    data: {
      companyName: {
        required
      },

      streetName: {
        required
      },

      streetNumber: {
        required
      },

      city: {
        required
      },

      postalCode: {
        required
      },

      kvkNumber: {
        numeric,
        minLength: minLength(8),
        maxLength: maxLength(8)
      },

      vatNumber: {},

      contactInitials: {
        required
      },

      contactLastName: {
        required
      },

      contactEmail: {
        required,
        email
      },

      contactPhone: {
        required,
        regex: (v) => fullPhoneRegex.test(v)
      }
    }
  },

  created () {
    if (this.data.dealerEnabled) this.expanded = 0
  }
}
</script>

<style lang="scss">
.tab-dealer {
  .v-expansion-panel .v-expansion-panel__container--disabled {
    color: inherit !important;
  }
}
</style>
